/**
 * EventsView — "Sales on important days" tab for /growth.
 *
 * Past events: actual day-of sales vs the 7-day surrounding average, with
 * lift % flagged green/red. Tells you which festivals/wellness days actually
 * moved the needle for Flax (and which were duds).
 *
 * Upcoming events: the next 90 days of calendar entries with the brand angle
 * for each, so the operator can plan campaigns.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { CalendarDays, Sparkles, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { formatINRCompact } from '@/lib/format';
import { cn } from '@/lib/utils';
import { marketing } from '@/lib/supabase/marketing';
import {
  MARKETING_CALENDAR,
  CATEGORY_META,
  type EventCategory,
  type MarketingEvent,
} from '@/lib/marketing-calendar';

interface DailyRow {
  date: string;
  net_sales: number | null;
}

const CATEGORIES: EventCategory[] = ['festival', 'wellness', 'retail', 'industry'];

export function EventsView() {
  const [activeCats, setActiveCats] = useState<Set<EventCategory>>(
    new Set<EventCategory>(['festival', 'wellness', 'retail', 'industry']),
  );

  const today = useMemo(() => new Date(), []);
  const todayISO = today.toISOString().slice(0, 10);

  // Pull daily pan-Flax net sales for the window we need (last ~14 months
  // → today + 90 days for forward planning context). Single fetch, bucketed.
  const since = useMemo(() => {
    const d = new Date(today);
    d.setDate(d.getDate() - 420);
    return d.toISOString().slice(0, 10);
  }, [today]);

  const daily = useQuery({
    queryKey: ['events-daily', since, todayISO],
    queryFn: async (): Promise<DailyRow[]> => {
      const { data, error } = await marketing
        .from('outlet_sales_v')
        .select('date, net_sales')
        .gte('date', since)
        .lte('date', todayISO)
        .range(0, 9999);
      if (error) throw error;
      return (data ?? []) as DailyRow[];
    },
    staleTime: 5 * 60_000,
  });

  // Bucket daily rows to a per-date pan-Flax total.
  const byDate = useMemo<Map<string, number>>(() => {
    const m = new Map<string, number>();
    for (const r of daily.data ?? []) {
      if (!r.date) continue;
      m.set(r.date, (m.get(r.date) ?? 0) + Number(r.net_sales ?? 0));
    }
    return m;
  }, [daily.data]);

  // For each past event compute the day's sales + a 7-day baseline.
  // Baseline = mean of ±3 days around the event (excluding the event itself,
  // and any other event in the window). Lift = (event − baseline) / baseline.
  const eventStats = useMemo(() => {
    const eventDates = new Set(MARKETING_CALENDAR.map((e) => e.date));
    return MARKETING_CALENDAR.map((e) => {
      const eventSales = byDate.get(e.date) ?? 0;
      // baseline window
      const center = new Date(e.date);
      const baselineDays: number[] = [];
      for (let off = -3; off <= 3; off++) {
        if (off === 0) continue;
        const d = new Date(center);
        d.setDate(d.getDate() + off);
        const iso = d.toISOString().slice(0, 10);
        if (eventDates.has(iso)) continue; // skip if another event neighbours
        const v = byDate.get(iso);
        if (v != null && v > 0) baselineDays.push(v);
      }
      const baseline =
        baselineDays.length > 0
          ? baselineDays.reduce((s, x) => s + x, 0) / baselineDays.length
          : 0;
      const lift = baseline > 0 ? ((eventSales - baseline) / baseline) * 100 : null;
      return { event: e, eventSales, baseline, lift };
    });
  }, [byDate]);

  const past = eventStats
    .filter((s) => s.event.date < todayISO && activeCats.has(s.event.category) && s.eventSales > 0)
    .sort((a, b) => (a.event.date < b.event.date ? 1 : -1));

  const upcoming = MARKETING_CALENDAR.filter((e) => {
    if (!activeCats.has(e.category)) return false;
    if (e.date < todayISO) return false;
    const ahead = new Date(e.date);
    const cutoff = new Date(today);
    cutoff.setDate(cutoff.getDate() + 90);
    return ahead <= cutoff;
  }).sort((a, b) => (a.date < b.date ? -1 : 1));

  // Headline: how big a lift do events give us on average?
  const liftSamples = past.filter((p) => p.lift != null).map((p) => p.lift!);
  const avgLift =
    liftSamples.length > 0
      ? liftSamples.reduce((s, x) => s + x, 0) / liftSamples.length
      : null;
  const bestPast = past.filter((p) => p.lift != null).sort((a, b) => (b.lift ?? 0) - (a.lift ?? 0))[0];
  const worstPast = past.filter((p) => p.lift != null).sort((a, b) => (a.lift ?? 0) - (b.lift ?? 0))[0];

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="space-y-4"
    >
      <CategoryFilter active={activeCats} setActive={setActiveCats} />

      <EventsHero
        avgLift={avgLift}
        best={bestPast}
        worst={worstPast}
        upcomingCount={upcoming.length}
        loading={daily.isLoading}
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <Section
          title="Upcoming · next 90 days"
          icon={<CalendarDays className="h-4 w-4 text-violet-500" />}
          empty={upcoming.length === 0 ? 'No events in the next 90 days for the selected categories.' : null}
        >
          <UpcomingList items={upcoming} todayISO={todayISO} />
        </Section>

        <Section
          title="Past · what actually moved the needle"
          icon={<Sparkles className="h-4 w-4 text-emerald-500" />}
          empty={
            daily.isLoading
              ? 'Loading…'
              : past.length === 0
                ? 'No past events with sales data for the selected categories.'
                : null
          }
        >
          <PastList items={past.slice(0, 30)} />
        </Section>
      </div>
    </motion.div>
  );
}

// ───────── Hero ─────────

function EventsHero({
  avgLift,
  best,
  worst,
  upcomingCount,
  loading,
}: {
  avgLift: number | null;
  best?: { event: MarketingEvent; lift: number | null };
  worst?: { event: MarketingEvent; lift: number | null };
  upcomingCount: number;
  loading: boolean;
}) {
  return (
    <Card className="overflow-hidden border-border/60 bg-gradient-to-br from-violet-500/[0.04] via-card to-card">
      <CardContent className="grid gap-4 p-5 md:grid-cols-4 md:p-6">
        <div>
          <div className="text-2xs uppercase tracking-wide text-muted-foreground">Avg event lift</div>
          <div
            className={cn(
              'mt-1 text-3xl font-semibold tabular-nums md:text-4xl',
              avgLift === null
                ? 'text-muted-foreground'
                : avgLift >= 5
                  ? 'text-emerald-500'
                  : avgLift >= -3
                    ? 'text-amber-500'
                    : 'text-rose-500',
            )}
          >
            {loading ? '…' : avgLift === null ? '—' : `${avgLift >= 0 ? '+' : ''}${avgLift.toFixed(1)}%`}
          </div>
          <div className="text-2xs text-muted-foreground">vs surrounding 7-day baseline</div>
        </div>
        <div>
          <div className="text-2xs uppercase tracking-wide text-muted-foreground">Best past event</div>
          {best?.lift != null ? (
            <>
              <div className="mt-1 text-base font-semibold leading-tight">{best.event.name}</div>
              <div className="text-2xs text-emerald-500 tabular-nums">+{best.lift.toFixed(1)}% on {pretty(best.event.date)}</div>
            </>
          ) : (
            <div className="mt-1 text-sm text-muted-foreground">—</div>
          )}
        </div>
        <div>
          <div className="text-2xs uppercase tracking-wide text-muted-foreground">Biggest drag</div>
          {worst?.lift != null ? (
            <>
              <div className="mt-1 text-base font-semibold leading-tight">{worst.event.name}</div>
              <div className="text-2xs text-rose-500 tabular-nums">{worst.lift.toFixed(1)}% on {pretty(worst.event.date)}</div>
            </>
          ) : (
            <div className="mt-1 text-sm text-muted-foreground">—</div>
          )}
        </div>
        <div>
          <div className="text-2xs uppercase tracking-wide text-muted-foreground">Upcoming · 90 days</div>
          <div className="mt-1 text-3xl font-semibold tabular-nums md:text-4xl">{upcomingCount}</div>
          <div className="text-2xs text-muted-foreground">opportunities to plan around</div>
        </div>
      </CardContent>
    </Card>
  );
}

// ───────── Category filter ─────────

function CategoryFilter({
  active,
  setActive,
}: {
  active: Set<EventCategory>;
  setActive: (next: Set<EventCategory>) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 text-2xs">
      <span className="text-muted-foreground">Filter:</span>
      {CATEGORIES.map((c) => {
        const meta = CATEGORY_META[c];
        const on = active.has(c);
        return (
          <button
            key={c}
            onClick={() => {
              const next = new Set(active);
              if (on) next.delete(c);
              else next.add(c);
              if (next.size === 0) return; // keep at least one on
              setActive(next);
            }}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-medium transition',
              on
                ? `${meta.bg} ${meta.color}`
                : 'bg-muted/40 text-muted-foreground hover:text-foreground',
            )}
          >
            <span className={cn('h-1.5 w-1.5 rounded-full', meta.bg)} />
            {meta.label}
          </button>
        );
      })}
    </div>
  );
}

// ───────── Lists ─────────

function UpcomingList({ items, todayISO }: { items: MarketingEvent[]; todayISO: string }) {
  return (
    <Card className="overflow-hidden border-border/60">
      <ul className="divide-y divide-border/30">
        {items.map((e) => {
          const days = daysBetween(todayISO, e.date);
          return (
            <li key={e.date + e.name} className="flex flex-col gap-1 p-3">
              <div className="flex items-center gap-2 text-sm">
                <CategoryChip cat={e.category} />
                <span className="font-medium">{e.name}</span>
                <span className="ml-auto text-2xs tabular-nums text-muted-foreground">
                  {pretty(e.date)} · {days === 0 ? 'today' : days === 1 ? 'tomorrow' : `in ${days}d`}
                </span>
              </div>
              <div className="text-2xs text-muted-foreground">{e.angle}</div>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

function PastList({
  items,
}: {
  items: Array<{ event: MarketingEvent; eventSales: number; baseline: number; lift: number | null }>;
}) {
  return (
    <Card className="overflow-hidden border-border/60">
      <ul className="divide-y divide-border/30">
        {items.map((p) => {
          const tone =
            p.lift === null
              ? 'na'
              : p.lift >= 5
                ? 'good'
                : p.lift >= -3
                  ? 'warn'
                  : 'bad';
          return (
            <li key={p.event.date + p.event.name} className="flex flex-col gap-1 p-3">
              <div className="flex items-center gap-2 text-sm">
                <CategoryChip cat={p.event.category} />
                <span className="font-medium">{p.event.name}</span>
                <span className="ml-auto inline-flex items-center gap-1 text-2xs tabular-nums text-muted-foreground">
                  {pretty(p.event.date)}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-3 text-2xs">
                <span className={cn('inline-flex items-center gap-1 font-semibold tabular-nums', toneClass(tone))}>
                  <ToneArrow tone={tone} />
                  {p.lift === null ? '—' : `${p.lift >= 0 ? '+' : ''}${p.lift.toFixed(1)}%`}
                </span>
                <span className="text-muted-foreground tabular-nums">
                  {formatINRCompact(p.eventSales)} day · {p.baseline > 0 ? `baseline ${formatINRCompact(p.baseline)}` : 'no baseline'}
                </span>
              </div>
              <div className="text-2xs text-muted-foreground">{p.event.angle}</div>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

// ───────── Bits ─────────

function CategoryChip({ cat }: { cat: EventCategory }) {
  const meta = CATEGORY_META[cat];
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium', meta.bg, meta.color)}>
      {meta.label}
    </span>
  );
}

function Section({
  title,
  icon,
  empty,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  empty: string | null;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
        {icon}
        {title}
      </div>
      {empty ? (
        <Card className="border-dashed border-border/60 bg-muted/20">
          <CardContent className="p-4 text-xs text-muted-foreground">{empty}</CardContent>
        </Card>
      ) : (
        children
      )}
    </div>
  );
}

function ToneArrow({ tone }: { tone: 'good' | 'warn' | 'bad' | 'na' }) {
  if (tone === 'good') return <TrendingUp className="h-3 w-3" />;
  if (tone === 'bad') return <TrendingDown className="h-3 w-3" />;
  return <Minus className="h-3 w-3" />;
}

function toneClass(tone: 'good' | 'warn' | 'bad' | 'na'): string {
  return tone === 'good'
    ? 'text-emerald-600 dark:text-emerald-400'
    : tone === 'bad'
      ? 'text-rose-600 dark:text-rose-400'
      : tone === 'warn'
        ? 'text-amber-600 dark:text-amber-400'
        : 'text-muted-foreground';
}

function pretty(iso: string): string {
  const parts = iso.split('-').map(Number);
  const y = parts[0] ?? 2000;
  const m = parts[1] ?? 1;
  const d = parts[2] ?? 1;
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' });
}

function daysBetween(fromISO: string, toISO: string): number {
  const a = new Date(fromISO);
  const b = new Date(toISO);
  return Math.round((b.getTime() - a.getTime()) / 86400000);
}
